/**
 * Public Profile Page - /u/$identifier
 *
 * Displays another user's profile with positions and activity.
 * Uses same data and UI as own profile, but without claim functionality.
 *
 * For viewing your OWN profile with claim actions, use /profile
 */

import { api } from "@breakout/backend/convex/_generated/api"
import { formatUsd } from "@breakout/ui/utils"
import { convexQuery } from "@convex-dev/react-query"
import { useQuery } from "@tanstack/react-query"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { zodSearchValidator } from "@tanstack/router-zod-adapter"
import { ArrowUpRight, Clock, LayoutGrid, TrendingUp, User } from "lucide-react"
import { useEffect, useMemo } from "react"
import type { Address } from "viem"
import { getAddress, isAddress } from "viem"
import { z } from "zod"
import { PageContainer } from "../components/layout/PageContainer"
import { PositionsSection } from "../components/profile/PositionsSection"
import { ProfileActivityFeed } from "../components/profile/ProfileActivityFeed"
import { ProfileHeader, ProfileStat } from "../components/profile/ProfileHeader"
import { ProfilePageSkeleton } from "../components/profile/ProfileSkeletons"
import { DEFAULT_POSITION_STATS, formatPnl } from "../components/profile/pnlUtils"
import { EmptyState } from "../components/ui/EmptyState"
import { ErrorState } from "../components/ui/ErrorState"
import { useDocumentTitle } from "../hooks/common/useDocumentTitle"
import { useUserPositionsEnhanced } from "../hooks/indexer/useUserPositionsEnhanced"
import { useAuthState } from "../hooks/user/useAuthState"
import { breakoutChain } from "../lib/providers"

const publicProfileSearchSchema = z.object({
	tab: z.enum(["positions", "activity"]).optional().catch(undefined),
	status: z.enum(["active", "closed"]).optional().catch(undefined),
})

type TabType = "positions" | "activity"

export const Route = createFileRoute("/u/$identifier")({
	component: PublicProfilePage,
	validateSearch: zodSearchValidator(publicProfileSearchSchema),
})

function PublicProfilePage() {
	// 1. Routing params
	const { identifier } = Route.useParams()
	const { tab, status } = Route.useSearch()
	const navigate = useNavigate()

	// 2. Context/store access
	const { evmWallet } = useAuthState()
	const currentUserAddress =
		evmWallet?.address && isAddress(evmWallet.address) ? getAddress(evmWallet.address) : undefined

	const activeTab: TabType = tab ?? "positions"
	const statusFilter: "active" | "closed" = status ?? "active"
	const isWalletAddress = isAddress(identifier)

	// 3. Data queries
	// First: resolve identifier to profile info (username → wallet address)
	const {
		data: profile,
		isLoading: isProfileLoading,
		error: profileError,
	} = useQuery(convexQuery(api.domains.profile.public.getPublicProfile, { identifier }))

	// Derive wallet address for data queries
	const walletAddress = useMemo(() => {
		if (profile?.walletAddress && isAddress(profile.walletAddress)) {
			return getAddress(profile.walletAddress)
		}
		if (isWalletAddress && isAddress(identifier)) {
			return getAddress(identifier)
		}
		return undefined
	}, [profile?.walletAddress, isWalletAddress, identifier])

	// Then: fetch positions using enhanced indexer query
	const {
		data: positionsData,
		isLoading: isPositionsLoading,
		error: positionsError,
	} = useUserPositionsEnhanced(walletAddress ? { userAddress: walletAddress } : null)

	// Check if viewing own profile - redirect to /profile for full data
	const isOwnProfile = Boolean(
		currentUserAddress &&
			walletAddress &&
			currentUserAddress.toLowerCase() === walletAddress.toLowerCase(),
	)

	// 4. Effects
	// Redirect wallet address to username if profile found
	useEffect(() => {
		if (isWalletAddress && profile?.username) {
			void navigate({
				to: "/u/$identifier",
				params: { identifier: profile.username },
				search: { tab, status },
				replace: true,
			})
		}
	}, [isWalletAddress, profile?.username, navigate, tab, status])

	// Redirect to /profile if viewing own profile (to see full data)
	useEffect(() => {
		if (isOwnProfile && !isProfileLoading) {
			void navigate({
				to: "/profile",
				search: { tab, status },
				replace: true,
			})
		}
	}, [isOwnProfile, isProfileLoading, navigate, tab, status])

	// 5. Derived values
	// Stats from backend
	const stats = positionsData?.stats ?? DEFAULT_POSITION_STATS

	// Update document title
	useDocumentTitle(profile ? `@${profile.username}` : null)

	// 6. Event handlers
	const setActiveTab = (newTab: TabType) => {
		void navigate({
			to: "/u/$identifier",
			params: { identifier },
			search: { tab: newTab === "positions" ? undefined : newTab, status },
			replace: true,
			resetScroll: false,
		})
	}

	const setStatusFilter = (newStatus: "active" | "closed") => {
		void navigate({
			to: "/u/$identifier",
			params: { identifier },
			search: { tab, status: newStatus === "active" ? undefined : newStatus },
			replace: true,
			resetScroll: false,
		})
	}

	// Loading state
	if (isProfileLoading) {
		return <ProfilePageSkeleton activeTab={activeTab} statusFilter={statusFilter} />
	}

	if (profileError) {
		return (
			<PageContainer className="py-16">
				<ErrorState
					backHref="/"
					backLabel="Back to markets"
					description="Unable to load profile. Please try again."
					icon={User}
					title="Error Loading Profile"
					variant="full"
				/>
			</PageContainer>
		)
	}

	if (!profile && !isWalletAddress) {
		return (
			<PageContainer className="py-16">
				<ErrorState
					backHref="/"
					backLabel="Back to markets"
					description="No user found with this username."
					icon={User}
					title="User Not Found"
					variant="full"
				/>
			</PageContainer>
		)
	}

	// Wallet address with no profile - show empty state
	if (!profile && isWalletAddress) {
		return (
			<PageContainer className="py-16">
				<EmptyState
					description="This wallet has no trading activity yet."
					icon={User}
					title="No Activity"
				/>
			</PageContainer>
		)
	}

	// At this point profile is guaranteed to exist (handled all null cases above)
	if (!profile) {
		return null
	}

	const displayUsername = profile.username ?? identifier
	// avatarUrl is always provided by the backend (with DiceBear fallback applied server-side)
	const avatarUrl = profile.avatarUrl
	const explorerUrl = walletAddress
		? `${breakoutChain.blockExplorers?.default?.url ?? "https://basescan.org"}/address/${walletAddress}`
		: null

	// Compute unique markets count
	const uniqueMarketsCount = isPositionsLoading
		? "—"
		: String(new Set(positionsData?.positions.map(p => p.marketAddress)).size)

	return (
		<>
			<ProfileHeader
				activeTab={activeTab}
				avatarUrl={avatarUrl}
				displayName={`@${displayUsername}`}
				explorerUrl={explorerUrl}
				onTabChange={setActiveTab}
				stats={
					<>
						{/* Positions Count Card */}
						<ProfileStat
							icon={
								<TrendingUp aria-hidden="true" className="size-3 text-text-muted sm:size-3.5" />
							}
							label="Positions"
							value={isPositionsLoading ? "—" : String(stats.activeCount + stats.closedCount)}
						/>

						{/* Portfolio Value Card */}
						<ProfileStat
							actions={
								!isPositionsLoading ? (
									<span
										className={`inline-flex items-center gap-0.5 rounded-md px-1.5 py-0.5 font-semibold text-xs tabular-nums sm:px-2 sm:py-1 ${
											stats.totalPnl > 0
												? "bg-emerald-500/10 text-emerald-600"
												: stats.totalPnl < 0
													? "bg-rose-500/10 text-rose-600"
													: "bg-text-muted/10 text-text-muted"
										}`}
									>
										{formatPnl(stats.totalPnl)}
									</span>
								) : undefined
							}
							icon={
								<ArrowUpRight aria-hidden="true" className="size-3 text-blue-500 sm:size-3.5" />
							}
							iconBgClass="bg-blue-500/10"
							label="Portfolio"
							value={isPositionsLoading ? "—" : formatUsd(stats.totalValue)}
						/>

						{/* Markets Traded Card */}
						<ProfileStat
							icon={
								<LayoutGrid aria-hidden="true" className="size-3 text-text-muted sm:size-3.5" />
							}
							label="Markets"
							value={uniqueMarketsCount}
						/>
					</>
				}
				variant="public"
			/>

			{/* Content */}
			<PageContainer className="pt-4 pb-8 sm:pt-6">
				<div aria-labelledby={`tab-${activeTab}`} id={`tabpanel-${activeTab}`} role="tabpanel">
					{activeTab === "positions" ? (
						<PositionsSection
							error={positionsError}
							isLoading={isPositionsLoading}
							onStatusFilterChange={setStatusFilter}
							positionsData={positionsData}
							statusFilter={statusFilter}
							variant="public"
							walletAddress={walletAddress ?? null}
						/>
					) : (
						<ActivityTab walletAddress={walletAddress ?? null} />
					)}
				</div>
			</PageContainer>
		</>
	)
}

function ActivityTab({ walletAddress }: { walletAddress: Address | null }) {
	if (!walletAddress) {
		return (
			<EmptyState
				description="This user has no trading activity"
				icon={Clock}
				title="No Activity"
			/>
		)
	}

	return <ProfileActivityFeed userAddress={walletAddress} />
}
